import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Calendar,
  XCircle,
  CheckCircle,
  Check,
  Loader2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotifications, useNotificationActions } from '@/store/notificationStore';
import { StaffNotification } from '@/types';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const typeConfig: Record<
  StaffNotification['type'],
  { icon: React.ReactNode; color: string }
> = {
  new_booking: {
    icon: <Calendar className="h-4 w-4" />,
    color: 'text-primary-500 bg-primary-50',
  },
  booking_cancelled: {
    icon: <XCircle className="h-4 w-4" />,
    color: 'text-red-500 bg-red-50',
  },
  booking_confirmed: {
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'text-green-500 bg-green-50',
  },
  booking_completed: {
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'text-primary-500 bg-primary-50',
  },
};

function useTimeAgo() {
  const { t } = useTranslation();

  return (dateStr: string): string => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return t('notifications.justNow');
    if (diffMin < 60) return t('notifications.minutesAgo', { count: diffMin });
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return t('notifications.hoursAgo', { count: diffHour });
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 30) return t('notifications.daysAgo', { count: diffDay });
    return date.toLocaleDateString();
  };
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose }) => {
  const { notifications, unreadCount, loading } = useNotifications();
  const { fetchNotifications, markAsRead, markAllAsRead } = useNotificationActions();
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const timeAgo = useTimeAgo();

  // 打开时加载通知
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleNotificationClick = async (notification: StaffNotification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    if (notification.booking_id) {
      const params = new URLSearchParams();
      params.set('highlight', String(notification.booking_id));
      if (notification.booking_date) {
        params.set('date', notification.booking_date.split('T')[0]);
      }
      navigate(`/bookings?${params.toString()}`);
      onClose();
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-hard border border-gray-200 z-50 overflow-hidden"
        >
          {/* 标题栏 */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('notifications.title')} {unreadCount > 0 && (
                <span className="ml-1 text-xs text-primary-500">({unreadCount})</span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          {/* 通知列表 */}
          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <Bell className="h-8 w-8 mb-2" />
                <p className="text-sm">{t('notifications.empty')}</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const config = typeConfig[notification.type];
                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                      !notification.is_read ? 'bg-primary-50/30' : ''
                    }`}
                  >
                    {/* 图标 */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${config.color}`}>
                      {config.icon}
                    </div>

                    {/* 内容 */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {t(`notifications.type_${notification.type}`, { name: notification.title })}
                      </p>
                      {notification.message && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {timeAgo(notification.created_at)}
                      </p>
                    </div>

                    {/* 未读标记 */}
                    {!notification.is_read && (
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-2 h-2 bg-primary-500 rounded-full" />
                      </div>
                    )}
                    {notification.is_read && (
                      <div className="flex-shrink-0 mt-1.5">
                        <Check className="w-3 h-3 text-gray-300" />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationCenter;
